import clsx from 'clsx';
import { memo, ReactNode, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useWishlist } from '~/features/commerce-api/hooks/wishlist/useWishList';
import { M31RaptorProductCarouselModule as M31RaptorCarouselModuleType } from '~/lib';
import { useRaptorProducts } from '~/services/raptor/useRaptorProducts';
import { useProductHandler } from '~/services/search/useProductHandler';
import { CardCarousel } from '~/shared/components/CardCarousel/CardCarousel';
import { NewProductCard, NewProductCardSkeleton } from '~/shared/components/NewProductCard';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { useEvents } from '~/shared/hooks/useEvents';
import { _transformProductDetailsToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { GTM_LIST_NAME } from '~/shared/hooks/useEvents/useGTMEvents/models';
import { useFrame } from '~/shared/utils';
import { ModuleContainer } from '../ModuleContainer';
import { useOptionalPDPData } from '~/features/product-details/context';
import styles from './M31RaptorProductCarousel.module.scss';

type M31Props = M31RaptorCarouselModuleType;

const M31RaptorProductCarouselBlockComponent = ({
    headline,
    callToAction,
    priority,
    raptorAlgorithm,
    raptorParameter,
    merchandisingCampaignId,
    isLastItem,
    ...rest
}: M31Props) => {
    const { data: frame } = useFrame();
    const { productsImpressionsEvent } = useEvents(frame);
    const { isPlusMember } = useCustomer();
    const productHandler = useProductHandler();
    const { handleFavourite, isFavorite } = useWishlist();

    const { ref: viewRef, inView } = useInView({ rootMargin: '1000px 0px', threshold: 0 });

    const { ref: trackingRef, inView: shouldTrackCarousel } = useInView({
        triggerOnce: true,
        threshold: 0,
        rootMargin: '0px 0px -500px 0px',
    });

    // This block can be placed on pages that have no product, so the context is optional.
    const pdpData = useOptionalPDPData();
    const product = pdpData?.product;

    // Cards sitting beside a full-size product are a secondary read, so they condense.
    const isCondensed = !!pdpData;
    const cardLayout = isCondensed ? 'condensed' : 'default';
    const cardClassName = clsx(styles.productCard, isCondensed && styles.isCondensed);
    const cardVariant = rest.backgroundColor ? 'opaque' : 'default';

    const {
        data: products,
        isSuccess,
        isError,
        isLoading,
    } = useRaptorProducts({
        config: raptorAlgorithm,
        param: raptorParameter,
        productId: product?.id,
        productBrand: product?.brand,
        productCategory: product?.c_ProductMainCategory,
        merchandisingCampaignId,
        preventAutoFetch: !inView,
        amount: isCondensed ? 15 : 10,
    });

    useEffect(() => {
        if (isSuccess && Array.isArray(products) && products.length > 0 && shouldTrackCarousel) {
            productsImpressionsEvent(
                raptorAlgorithm,
                `${GTM_LIST_NAME.RAPTOR} -  ${headline?.text}`,
                products.map((item: TProductDetails, index: number) =>
                    _transformProductDetailsToDataLayer(item, 1, index, isPlusMember),
                ),
            );
        }
    }, [shouldTrackCarousel]);

    if (isError || (isSuccess && (!Array.isArray(products) || products.length === 0))) {
        return null;
    }

    const renderItems = (): ReactNode[] => {
        if (!isSuccess && isLoading) {
            return [...Array(5)].map((_, index) => (
                <NewProductCardSkeleton
                    key={`skeleton-${index}`}
                    className={cardClassName}
                    variant={cardVariant}
                    layout={cardLayout}
                />
            ));
        }

        if (!Array.isArray(products)) {
            return [];
        }

        return products.map((product: TProductDetails, index: number) => (
            <NewProductCard
                className={cardClassName}
                key={`${product.id}-${index}`}
                product={product}
                imagePriority={priority}
                position={index}
                trackingListId={raptorAlgorithm}
                trackingListName={`${GTM_LIST_NAME.RAPTOR} - ${headline?.text}`}
                onButtonClick={productHandler}
                onFavourite={handleFavourite}
                isFavourite={isFavorite(product.id)}
                raptorAlgorithm={raptorAlgorithm}
                variant={cardVariant}
                layout={cardLayout}
            />
        ));
    };

    const items = renderItems();

    return (
        <ModuleContainer {...rest}>
            <div ref={trackingRef}></div>

            <div ref={viewRef}>
                <CardCarousel
                    isCondensed={isCondensed}
                    headlineVariant={isCondensed ? 'title-XS' : undefined}
                    headline={headline}
                    callToAction={!isLoading && isSuccess ? callToAction : undefined}
                    items={items}
                    isSkipHidden={isLastItem}
                />
            </div>
        </ModuleContainer>
    );
};

export const M31RaptorProductCarouselBlock = memo(M31RaptorProductCarouselBlockComponent);
